import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  Hotel as HotelIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  EventAvailable as ActivityIcon,
  Assignment as RoomingIcon,
  LocalOffer as DiscountIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

interface GroupReservation {
  _id: string;
  groupName: string;
  groupType: string;
  primaryContactId: any;
  locationId: any;
  status: string;
  totalSites: number;
  reservedSites: number;
  arrivalDate: string;
  departureDate: string;
  groupDiscount: {
    type: string;
    value: number;
  };
  depositRequirements: {
    depositPercentage: number;
    depositDueDate: string;
    depositPaid: boolean;
    depositAmount: number;
  };
  blockReleaseDate: string;
  financialSummary: {
    totalEstimatedRevenue: number;
    totalActualRevenue: number;
    totalDepositsReceived: number;
    totalRefundsIssued: number;
  };
}

interface GroupMember {
  _id: string;
  memberName: string;
  memberEmail: string;
  memberPhone: string;
  status: string;
  sitePreference: string;
  specialNeeds: string;
  reservationId?: any;
}

const GroupReservationsPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [groups, setGroups] = useState<GroupReservation[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupReservation | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [createDialog, setCreateDialog] = useState(false);
  const [memberDialog, setMemberDialog] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form state for new group
  const [formData, setFormData] = useState({
    groupName: '',
    groupType: 'family_reunion',
    primaryContactId: '',
    locationId: '',
    totalSites: 10,
    arrivalDate: null,
    departureDate: null,
    groupDiscount: {
      type: 'none',
      value: 0
    },
    depositRequirements: {
      depositPercentage: 25
    },
    specialRequirements: {
      pavilionNeeded: false,
      cateringRequired: false,
      audioVisualNeeded: false
    }
  });

  const steps = ['Group Details', 'Requirements', 'Financial', 'Review'];

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupDetails(selectedGroup._id);
    }
  }, [selectedGroup]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/group-reservations');
      setGroups(response.data.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId: string) => {
    try {
      const [membersRes, analyticsRes] = await Promise.all([
        api.get(`/api/group-reservations/${groupId}/members`),
        api.get(`/api/group-reservations/${groupId}/analytics`)
      ]);
      
      setMembers(membersRes.data.data);
      setAnalytics(analyticsRes.data.data);
    } catch (error) {
      console.error('Error fetching group details:', error);
    }
  };

  const handleCreateGroup = async () => {
    try {
      const response = await api.post('/api/group-reservations', formData);
      if (response.data.success) {
        setCreateDialog(false);
        fetchGroups();
        // Reset form
        setActiveStep(0);
      }
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const handleAddMembers = async (membersList: any[]) => {
    try {
      const response = await api.post(
        `/api/group-reservations/${selectedGroup?._id}/members`,
        { members: membersList }
      );
      if (response.data.success) {
        setMemberDialog(false);
        fetchGroupDetails(selectedGroup!._id);
      }
    } catch (error) {
      console.error('Error adding members:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'partial':
        return 'info';
      default:
        return 'default';
    }
  };

  const getGroupTypeIcon = (type: string) => {
    switch (type) {
      case 'family_reunion':
        return <GroupsIcon />;
      case 'corporate':
        return <ReceiptIcon />;
      case 'wedding':
        return <CheckIcon />;
      case 'rally':
        return <HotelIcon />;
      default:
        return <GroupsIcon />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Group Reservations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          New Group Reservation
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="All Groups" icon={<GroupsIcon />} iconPosition="start" />
          <Tab label="Active Groups" icon={<CheckIcon />} iconPosition="start" />
          <Tab label="Upcoming" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* All Groups Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={selectedGroup ? 7 : 12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Arrival</TableCell>
                    <TableCell>Sites</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Revenue</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((group) => (
                      <TableRow
                        key={group._id}
                        hover
                        selected={selectedGroup?._id === group._id}
                        onClick={() => setSelectedGroup(group)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getGroupTypeIcon(group.groupType)}
                            <Box sx={{ ml: 2 }}>
                              <Typography variant="subtitle2">
                                {group.groupName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.primaryContactId?.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{group.groupType}</TableCell>
                        <TableCell>
                          {dayjs(group.arrivalDate).format('MMM D, YYYY')}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {group.reservedSites}/{group.totalSites}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(group.reservedSites / group.totalSites) * 100}
                              sx={{ height: 4, mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={group.status}
                            size="small"
                            color={getStatusColor(group.status)}
                          />
                        </TableCell>
                        <TableCell>
                          {formatCurrency(group.financialSummary.totalEstimatedRevenue)}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={groups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableContainer>
          </Grid>

          {selectedGroup && (
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedGroup.groupName}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography variant="body2">
                        {selectedGroup.primaryContactId?.name}
                      </Typography>
                      <Typography variant="caption">
                        {selectedGroup.primaryContactId?.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Dates
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(selectedGroup.arrivalDate).format('MMM D')} - 
                        {dayjs(selectedGroup.departureDate).format('MMM D, YYYY')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {analytics && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Booking Progress
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {analytics.bookingProgress.bookedSites} of {analytics.bookingProgress.totalSites} sites
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {analytics.bookingProgress.percentageBooked}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(analytics.bookingProgress.percentageBooked)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Members
                            </Typography>
                            <Typography variant="h6">
                              {analytics.memberStatus.total}
                            </Typography>
                            <Typography variant="caption">
                              {analytics.memberStatus.booked} booked
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Revenue
                            </Typography>
                            <Typography variant="h6">
                              {formatCurrency(analytics.financial.actualRevenue)}
                            </Typography>
                            <Typography variant="caption">
                              {formatCurrency(analytics.financial.deposits)} deposits
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setMemberDialog(true)}
                      >
                        Add Members
                      </Button>
                      <Button
                        size="small"
                        startIcon={<RoomingIcon />}
                      >
                        Rooming List
                      </Button>
                      <Button
                        size="small"
                        startIcon={<ActivityIcon />}
                      >
                        Activities
                      </Button>
                      <Button
                        size="small"
                        startIcon={<ReceiptIcon />}
                      >
                        Invoice
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>

              {members.length > 0 && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Group Members ({members.length})
                  </Typography>
                  <List dense>
                    {members.slice(0, 5).map((member) => (
                      <ListItem key={member._id}>
                        <ListItemIcon>
                          <Badge
                            color={member.status === 'booked' ? 'success' : 'warning'}
                            variant="dot"
                          >
                            <PersonAddIcon />
                          </Badge>
                        </ListItemIcon>
                        <ListItemText
                          primary={member.memberName}
                          secondary={member.memberEmail}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={member.status}
                            size="small"
                            color={member.status === 'booked' ? 'success' : 'default'}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  {members.length > 5 && (
                    <Button fullWidth size="small">
                      View All Members
                    </Button>
                  )}
                </Paper>
              )}
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Active Groups Tab */}
        <Grid container spacing={3}>
          {groups
            .filter(g => g.status === 'confirmed' || g.status === 'partial')
            .map((group) => (
              <Grid item xs={12} md={6} lg={4} key={group._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {group.groupName}
                      </Typography>
                      <Chip
                        label={group.status}
                        size="small"
                        color={getStatusColor(group.status)}
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Arrival
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(group.arrivalDate).format('MMM D, YYYY')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2">
                          {group.reservedSites}/{group.totalSites} sites
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(group.reservedSites / group.totalSites) * 100}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button size="small" startIcon={<EmailIcon />}>
                        Email
                      </Button>
                      <Button size="small" startIcon={<ViewIcon />}>
                        Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Upcoming Groups Tab */}
        <Grid container spacing={3}>
          {groups
            .filter(g => new Date(g.arrivalDate) > new Date())
            .sort((a, b) => new Date(a.arrivalDate).getTime() - new Date(b.arrivalDate).getTime())
            .map((group) => (
              <Grid item xs={12} key={group._id}>
                <Paper sx={{ p: 3 }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getGroupTypeIcon(group.groupType)}
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="h6">
                            {group.groupName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.primaryContactId?.name}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Arrival Date
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(group.arrivalDate).format('MMMM D, YYYY')}
                      </Typography>
                      <Typography variant="caption">
                        {dayjs(group.arrivalDate).diff(dayjs(), 'days')} days away
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        Block Release
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(group.blockReleaseDate).format('MMM D')}
                      </Typography>
                      {new Date(group.blockReleaseDate) < new Date() && (
                        <Chip
                          label="Released"
                          size="small"
                          color="warning"
                        />
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Booking Status
                        </Typography>
                        <Typography variant="h6">
                          {group.reservedSites}/{group.totalSites}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(group.reservedSites / group.totalSites) * 100}
                          sx={{ height: 4, mt: 1 }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={1}>
                      <IconButton>
                        <ViewIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Analytics Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Groups
                </Typography>
                <Typography variant="h3">
                  {groups.filter(g => g.status === 'confirmed' || g.status === 'partial').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently hosting
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Sites Reserved
                </Typography>
                <Typography variant="h3">
                  {groups.reduce((sum, g) => sum + g.reservedSites, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across all groups
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Average Group Size
                </Typography>
                <Typography variant="h3">
                  {groups.length > 0 
                    ? Math.round(groups.reduce((sum, g) => sum + g.totalSites, 0) / groups.length)
                    : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sites per group
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h3">
                  {formatCurrency(
                    groups.reduce((sum, g) => sum + g.financialSummary.totalActualRevenue, 0)
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  From group bookings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Create Group Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Group Reservation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Group Name"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Group Type</InputLabel>
                    <Select
                      value={formData.groupType}
                      onChange={(e) => setFormData({ ...formData, groupType: e.target.value })}
                    >
                      <MenuItem value="family_reunion">Family Reunion</MenuItem>
                      <MenuItem value="corporate">Corporate Event</MenuItem>
                      <MenuItem value="wedding">Wedding</MenuItem>
                      <MenuItem value="rally">Rally</MenuItem>
                      <MenuItem value="tournament">Tournament</MenuItem>
                      <MenuItem value="club">Club</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of Sites"
                    type="number"
                    value={formData.totalSites}
                    onChange={(e) => setFormData({ ...formData, totalSites: parseInt(e.target.value) })}
                    InputProps={{
                      inputProps: { min: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Arrival Date"
                      value={formData.arrivalDate}
                      onChange={(newValue) => setFormData({ ...formData, arrivalDate: newValue })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Departure Date"
                      value={formData.departureDate}
                      onChange={(newValue) => setFormData({ ...formData, departureDate: newValue })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            )}

            {activeStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Special Requirements
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.specialRequirements.pavilionNeeded}
                        onChange={(e) => setFormData({
                          ...formData,
                          specialRequirements: {
                            ...formData.specialRequirements,
                            pavilionNeeded: e.target.checked
                          }
                        })}
                      />
                      Pavilion Needed
                    </label>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.specialRequirements.cateringRequired}
                        onChange={(e) => setFormData({
                          ...formData,
                          specialRequirements: {
                            ...formData.specialRequirements,
                            cateringRequired: e.target.checked
                          }
                        })}
                      />
                      Catering Required
                    </label>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.specialRequirements.audioVisualNeeded}
                        onChange={(e) => setFormData({
                          ...formData,
                          specialRequirements: {
                            ...formData.specialRequirements,
                            audioVisualNeeded: e.target.checked
                          }
                        })}
                      />
                      Audio/Visual Equipment
                    </label>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {activeStep === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={formData.groupDiscount.type}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupDiscount: {
                          ...formData.groupDiscount,
                          type: e.target.value
                        }
                      })}
                    >
                      <MenuItem value="none">No Discount</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                      <MenuItem value="fixed">Fixed Amount</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.groupDiscount.type !== 'none' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={formData.groupDiscount.type === 'percentage' ? 'Discount %' : 'Discount Amount'}
                      type="number"
                      value={formData.groupDiscount.value}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupDiscount: {
                          ...formData.groupDiscount,
                          value: parseFloat(e.target.value)
                        }
                      })}
                      InputProps={{
                        startAdornment: formData.groupDiscount.type === 'fixed' ? 
                          <InputAdornment position="start">$</InputAdornment> : null,
                        endAdornment: formData.groupDiscount.type === 'percentage' ? 
                          <InputAdornment position="end">%</InputAdornment> : null
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Deposit Percentage"
                    type="number"
                    value={formData.depositRequirements.depositPercentage}
                    onChange={(e) => setFormData({
                      ...formData,
                      depositRequirements: {
                        ...formData.depositRequirements,
                        depositPercentage: parseInt(e.target.value)
                      }
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100 }
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {activeStep === 3 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please review the group reservation details before creating.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Group Name
                    </Typography>
                    <Typography variant="body1">{formData.groupName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Group Type
                    </Typography>
                    <Typography variant="body1">{formData.groupType}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Sites
                    </Typography>
                    <Typography variant="body1">{formData.totalSites}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Dates
                    </Typography>
                    <Typography variant="body1">
                      {formData.arrivalDate && formData.departureDate && 
                        `${dayjs(formData.arrivalDate).format('MMM D')} - ${dayjs(formData.departureDate).format('MMM D, YYYY')}`
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep - 1)}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setActiveStep(activeStep + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateGroup}
            >
              Create Group
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog
        open={memberDialog}
        onClose={() => setMemberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Group Members</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Add members by entering their details below. They will receive an invitation to book their site.
          </Alert>
          {/* Member form would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />}>
            Send Invitations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupReservationsPage;